import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/apiClient";
import { PriceHistoryChart } from "../components/analytics/PriceHistoryChart";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import type {
  ItemPriceHistory,
  Supplier,
  SupplierBatchQualityRow,
  SupplierRankingRow,
  YieldRow,
} from "../api/types";
import { CostNote } from "../components/ui/CostNote";
import { unpricedQtyNotCountedNote } from "../utils/unknownCost";

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });

export function YieldInsightsPage() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState<YieldRow[]>([]);
  const [ranking, setRanking] = useState<SupplierRankingRow[]>([]);
  const [batchQuality, setBatchQuality] = useState<SupplierBatchQualityRow[]>([]);
  const [priceHistory, setPriceHistory] = useState<ItemPriceHistory | null>(null);
  const [priceItemId, setPriceItemId] = useState("");
  // Defaults to every supplier: "how does this price move" is a cross-supplier
  // question, and an item rarely has repeat batches from a single supplier.
  const [priceThisSupplierOnly, setPriceThisSupplierOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pricePanelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .listSuppliers()
      .then((list) => {
        if (cancelled) return;
        setSuppliers(list);
        if (list[0]) setSupplierId(list[0].id);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load suppliers");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadYield = useCallback(async () => {
    if (!supplierId) return;
    setError(null);
    try {
      const range = { from: from || undefined, to: to || undefined };
      const [data, rankingRows, qualityRows] = await Promise.all([
        api.getSupplierYield(supplierId, range),
        api.getSupplierRanking(range),
        api.getSupplierBatchQuality(supplierId, range),
      ]);
      setRows(data);
      setRanking(rankingRows);
      setBatchQuality(qualityRows);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load yield");
    }
  }, [supplierId, from, to]);

  useEffect(() => {
    if (supplierId) void loadYield();
  }, [supplierId, loadYield]);

  const loadPriceHistory = useCallback(
    async (itemId: string, thisSupplierOnly: boolean) => {
      setPriceItemId(itemId);
      setPriceThisSupplierOnly(thisSupplierOnly);
      if (!itemId) {
        setPriceHistory(null);
        return;
      }
      try {
        setPriceHistory(
          await api.getItemPriceHistory(itemId, {
            supplierId: thisSupplierOnly ? supplierId || undefined : undefined,
          }),
        );
        // The panel sits below a long table, so without this the click looks
        // like it did nothing.
        requestAnimationFrame(() =>
          pricePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        );
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Failed to load price history");
      }
    },
    [supplierId],
  );

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <h2 className="page-title">Yield insights</h2>
        <p className="page-subtitle">
          Supplier honesty: ranking across suppliers, invoiced vs consumed,
          out-of-box damage per batch, and how prices move over time.
        </p>
      </div>
      {error ? <div className="alert error">{error}</div> : null}

      {/* Supplier ranking */}
      <div className="card table-scroll">
        <strong>Supplier ranking — least out-of-box damage first</strong>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Supplier</th>
              <th>Deliveries</th>
              <th>Invoiced units</th>
              <th>Bad out of box</th>
              <th>Bad %</th>
              <th>Total spend</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((r, i) => (
              <tr
                key={r.supplierId}
                style={r.supplierId === supplierId ? { fontWeight: 600 } : undefined}
              >
                <td>{i + 1}</td>
                <td>{r.name}</td>
                <td>{r.batchCount}</td>
                <td>{r.invoicedQty}</td>
                <td>{r.badQty}</td>
                <td>{r.badPct != null ? `${r.badPct.toFixed(1)}%` : "—"}</td>
                <td>€{r.totalSpendEur}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card form-grid" style={{ maxWidth: 720 }}>
        <div className="field">
          <label>Supplier</label>
          <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>From (optional)</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label>To (optional)</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="field" style={{ alignSelf: "end" }}>
          <button type="button" className="button" onClick={() => void loadYield()}>
            Apply range
          </button>
        </div>
      </div>

      <div className="card table-scroll">
        <strong>Yield per item</strong>
        <table>
          <thead>
            <tr>
              <th>Item</th>
              <th>Invoiced</th>
              <th>Usable</th>
              <th>Consumed</th>
              <th>Waste*</th>
              <th>Yield %</th>
              <th>Box-open €</th>
              <th>True cost €</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.itemId}>
                <td>{r.itemName}</td>
                <td>{r.invoicedQty}</td>
                <td>{r.usableQty}</td>
                <td>{r.consumedQty}</td>
                <td>{r.wasteQtyExclReceiving}</td>
                <td>{r.yieldPct != null ? r.yieldPct.toFixed(1) : "—"}</td>
                <td>
                  {r.boxOpenCostEur ?? "—"}
                  {/* WO-B part 6: both costs are priced lots' money over priced units. */}
                  <CostNote note={unpricedQtyNotCountedNote(r.unpricedUsableQty)} />
                </td>
                <td>{r.trueCostEur ?? "—"}</td>
                <td>
                  <button
                    type="button"
                    className="button plain"
                    onClick={() => void loadPriceHistory(r.itemId, false)}
                  >
                    Price history
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: "var(--space-3)" }}>
          * Waste excludes receiving damage (already reflected in usable quantity).
        </p>
      </div>

      {/* Price history for one item */}
      {priceHistory && priceItemId ? (
        <div className="card table-scroll" ref={pricePanelRef}>
          <div className="price-chart-panel-header">
            <strong>Price over time — {priceHistory.itemName}</strong>
            <div className="price-chart-panel-actions">
              <SegmentedControl
                ariaLabel="Which suppliers to chart"
                value={priceThisSupplierOnly ? "one" : "all"}
                options={[
                  { value: "all", label: "All suppliers" },
                  { value: "one", label: "This supplier" },
                ]}
                onChange={(next) =>
                  void loadPriceHistory(priceItemId, next === "one")
                }
              />
              <button
                type="button"
                className="button plain"
                onClick={() => void loadPriceHistory("", false)}
              >
                Close
              </button>
            </div>
          </div>
          {priceHistory.points.length === 0 ? (
            <p className="muted">No priced purchases recorded.</p>
          ) : (
            <>
              <PriceHistoryChart
                itemName={priceHistory.itemName}
                points={priceHistory.points}
              />
              {priceHistory.points.length === 1 ? (
                <p className="price-chart-note">
                  Only one priced delivery of this item has been received{" "}
                  {priceThisSupplierOnly ? "from this supplier" : "so far"} — there
                  is no trend to draw yet.{" "}
                  {priceThisSupplierOnly
                    ? "Switch to All suppliers to compare."
                    : "The line appears from the second batch on."}
                </p>
              ) : null}
              <table>
                <thead>
                  <tr>
                    <th>Received</th>
                    <th>Supplier</th>
                    <th>Invoiced qty</th>
                    <th>Quoted €/unit</th>
                    <th>Box-open €/unit</th>
                  </tr>
                </thead>
                <tbody>
                  {priceHistory.points.map((p) => (
                    <tr key={p.batchId}>
                      <td>{fmtDate(p.receivedAt)}</td>
                      <td className="muted">{p.supplierName ?? "—"}</td>
                      <td>{p.invoicedQty}</td>
                      <td>{p.quotedUnitPriceEur ?? "—"}</td>
                      <td>{p.unitCostEur}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      ) : null}

      {/* Batch quality for the selected supplier */}
      <div className="card table-scroll">
        <strong>Batches from this supplier — out-of-box damage per delivery</strong>
        {batchQuality.length === 0 ? (
          <p className="muted">No batches in this range.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Received</th>
                <th>Item</th>
                <th>Invoiced</th>
                <th>Usable</th>
                <th>Bad</th>
                <th>Bad %</th>
                <th>Quoted €/unit</th>
                <th>Box-open €/unit</th>
              </tr>
            </thead>
            <tbody>
              {batchQuality.map((b) => (
                <tr key={b.batchId}>
                  <td>{fmtDate(b.receivedAt)}</td>
                  <td>{b.itemName}</td>
                  <td>{b.invoicedQty}</td>
                  <td>{b.usableQty}</td>
                  <td>{b.badQty}</td>
                  <td>
                    {b.badPct != null ? (
                      <span className={b.badPct > 10 ? "badge danger" : "badge"}>
                        {b.badPct.toFixed(1)}%
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{b.quotedUnitPriceEur ?? "—"}</td>
                  <td>{b.unitCostEur}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
